import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Card, CardContent, TextField, Typography, Link, Alert, Divider } from '@mui/material';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../api/client.js';
import { useLang } from '../i18n/lang.js';
import { AUTH_COPY } from '../i18n/authCopy.js';
import { LangToggle } from '../i18n/LangToggle.js';
import { useRecaptcha } from '../components/Recaptcha.js';
import type { RecaptchaConfig } from '../lib/recaptcha.js';

interface Meta {
  singleTenant: boolean;
  registrationOpen: boolean;
  googleEnabled: boolean;
  recaptcha?: RecaptchaConfig;
}

/** The Google "G" mark (MUI ships no brand icons). */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export function LoginPage() {
  const [lang] = useLang();
  const c = AUTH_COPY[lang].login;
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Start on the register tab when the user arrived via /register.
  const [mode, setMode] = useState<'login' | 'register'>(location.pathname === '/register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState<Meta | null>(null);
  // What we know about this address's identity provider, if anything.
  const [sso, setSso] = useState<{ available: boolean; enforced: boolean }>({
    available: false,
    enforced: false,
  });

  // Adapt to the deployment posture: hide open registration once it's closed,
  // and drop the org-name field on single-tenant (self-hosted) installs.
  useEffect(() => {
    api
      .get('/v1/meta')
      .then((r) => setMeta(r.data as Meta))
      .catch(() => setMeta({ singleTenant: false, registrationOpen: true, googleEnabled: false }));
  }, []);

  // Surface a failed/cancelled federated redirect. An SSO failure carries the
  // provider's own reason: "sign-in failed" with no cause is what turns an IdP
  // rollout into a support ticket.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get('error') === 'google') {
      setError(c.googleFailed);
    } else if (q.get('error') === 'sso') {
      setError(q.get('reason') || c.ssoFailed);
    }
  }, [location.search]);

  const captcha = useRecaptcha(meta?.recaptcha ?? null, mode === 'login' ? 'login' : 'register');

  const singleTenant = meta?.singleTenant ?? false;
  const canRegister = meta?.registrationOpen ?? false;
  if (mode === 'register' && meta && !canRegister) setMode('login');

  /**
   * Asks whether this address belongs to a configured identity provider. The
   * answer describes the domain, not the person, so it reveals nothing about
   * whether an account exists.
   */
  const checkSso = async (value: string) => {
    if (mode !== 'login' || !value.includes('@')) {
      setSso({ available: false, enforced: false });
      return;
    }
    try {
      const r = await api.post('/v1/auth/sso/check', { email: value });
      setSso(r.data as { available: boolean; enforced: boolean });
    } catch {
      setSso({ available: false, enforced: false });
    }
  };

  const startSso = async () => {
    if (!email.includes('@')) {
      setError(c.ssoEnterEmail);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const r = await api.post('/v1/auth/sso/start', { email });
      window.location.href = (r.data as { authorizeUrl: string }).authorizeUrl;
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data
        ?.error?.message;
      setError(msg || c.ssoNotConfigured);
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const recaptchaToken = await captcha.getToken();
      if (mode === 'login') await login(email, password, recaptchaToken);
      // In single-tenant mode the server ignores the org name (one org is
      // auto-seeded), so send a placeholder to satisfy the request schema.
      else
        await register({
          email,
          password,
          organizationName: singleTenant ? 'default' : orgName,
          recaptchaToken,
        });
      navigate('/dashboard');
    } catch (err) {
      const apiError = (
        err as {
          response?: {
            data?: {
              error?: {
                message?: string;
                details?: Array<{ path: string; message: string }>;
              };
            };
          };
        }
      ).response?.data?.error;
      // Surface the specific field problems (e.g. "password: min 10 chars")
      // instead of the generic "Validation failed".
      const detail = apiError?.details?.map((d) => `${d.path}: ${d.message}`).join(' · ');
      setError(detail || apiError?.message || c.somethingWrong);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
      <Card sx={{ width: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <LangToggle sx={{ float: 'right', mt: -1, mr: -1 }} />
          <Typography variant="h5" gutterBottom>
            Replay<span style={{ color: '#2ec2b3' }}>Ops</span>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {mode === 'login'
              ? c.signInTitle
              : singleTenant
                ? c.registerTitleSingle
                : c.registerTitleSaas}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {meta?.googleEnabled && (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleG />}
                onClick={() => {
                  window.location.href = '/v1/auth/google';
                }}
                sx={{ mb: 2, color: 'text.primary', borderColor: 'divider' }}
              >
                {c.continueWithGoogle}
              </Button>
              <Divider sx={{ mb: 2 }}>{c.or}</Divider>
            </>
          )}
          <form onSubmit={submit}>
            {mode === 'register' && !singleTenant && (
              <TextField
                label={c.orgName}
                fullWidth
                margin="normal"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
              />
            )}
            <TextField
              label={c.email}
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => void checkSso(e.target.value)}
              required
            />
            {/* An enforced domain has no password to type, showing the field
                would only invite the one attempt the server must refuse. */}
            {!sso.enforced && (
              <TextField
                label={c.password}
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            )}
            {sso.enforced && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {c.ssoEnforced}
              </Alert>
            )}
            {mode === 'login' && !sso.enforced && (
              <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                <Link component="button" type="button" variant="body2" onClick={() => navigate('/forgot-password')}>
                  {c.forgot}
                </Link>
              </Box>
            )}
            {captcha.node}
            {!sso.enforced && (
              <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={busy}>
                {mode === 'login' ? c.signIn : c.createAccount}
              </Button>
            )}
          </form>
          {mode === 'login' && (
            <Button
              fullWidth
              variant={sso.enforced ? 'contained' : 'text'}
              disabled={busy}
              onClick={() => void startSso()}
              sx={{ mt: sso.enforced ? 2 : 1 }}
            >
              {sso.available ? c.ssoContinue : c.ssoButton}
            </Button>
          )}
          {(canRegister || mode === 'register') && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {mode === 'login' ? c.noAccount : c.haveAccount}
              <Link component="button" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
                {mode === 'login' ? c.register : c.signInLink}
              </Link>
            </Typography>
          )}
          {meta && !canRegister && mode === 'login' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {c.registrationClosed}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
